import { WBTC_ADDRESSES, WRAPPED_NATIVE_ADDRESSES } from '@/constants/addresses';

import { TokenResult } from '@/utils/0x/token-registry.types';
import { isTokenAddressNativeAsset, isUsdStableAddress } from '@/utils/multichain';
import { normalizeTokenAddress } from '@/utils/normalizeAddress';

export type SlippageTier =
  | 'stable'
  | 'major'
  | 'mid'
  | 'longtail'
  | 'volatile'
  | 'emerging'
  | 'nascent';

// Liquidity-score (0-10) cutoffs: >= MAJOR is major, [MID, MAJOR) is mid, below is longtail.
export const MAJOR_LIQUIDITY_SCORE = 5;
export const MID_LIQUIDITY_SCORE = 3;

// Longtail legs escalate by pool age; youngest-first, first match wins.
// `volatile` is a legacy name — all three tiers are age-based.
const AGE_TIERS = [
  { tier: 'nascent', maxAgeSeconds: 8 * 60 * 60 },
  { tier: 'emerging', maxAgeSeconds: 24 * 60 * 60 },
  { tier: 'volatile', maxAgeSeconds: 48 * 60 * 60 },
] as const satisfies ReadonlyArray<{ tier: SlippageTier; maxAgeSeconds: number }>;

/**
 * `liquidityScore` and `ageSeconds` are fetched separately and attached by the caller
 * (undefined while unknown / loading). `ageSeconds` = seconds since the token's earliest
 * indexed pool was created; unknown age never escalates (fail-safe).
 */
export type ClassifierToken = Pick<TokenResult, 'address'> & {
  liquidityScore?: number | null;
  ageSeconds?: number | null;
};

const TIER_RANK: Record<SlippageTier, number> = {
  stable: 0,
  major: 1,
  mid: 2,
  longtail: 3,
  volatile: 4,
  emerging: 5,
  nascent: 6,
};

const score = (token: ClassifierToken): number | undefined =>
  typeof token.liquidityScore === 'number' ? token.liquidityScore : undefined;

const isMajor = (token: ClassifierToken, chainId: number): boolean => {
  const address = normalizeTokenAddress(token.address, chainId);
  const matches = (candidate: string | undefined): boolean =>
    !!candidate && address === normalizeTokenAddress(candidate, chainId);

  // The shared helper also covers native aliases (e.g. POL 0x...1010 on Polygon).
  if (isTokenAddressNativeAsset(token.address, chainId)) return true;
  if (matches(WRAPPED_NATIVE_ADDRESSES[chainId])) return true;
  if (matches(WBTC_ADDRESSES[chainId])) return true;
  return (score(token) ?? 0) >= MAJOR_LIQUIDITY_SCORE;
};

const ageTier = ({ ageSeconds }: ClassifierToken): SlippageTier | undefined => {
  // Unknown age never escalates (fail-safe).
  if (typeof ageSeconds !== 'number' || ageSeconds < 0) return undefined;
  return AGE_TIERS.find((tier) => ageSeconds < tier.maxAgeSeconds)?.tier;
};

/** Unknown / unfetched tokens fall to `longtail` (fail-safe); only known addresses are ever `stable`. */
export const tokenTier = (
  token: ClassifierToken | undefined | null,
  chainId: number,
): SlippageTier => {
  if (!token) return 'longtail';
  if (isUsdStableAddress(token.address, chainId)) return 'stable';
  if (isMajor(token, chainId)) return 'major';
  if ((score(token) ?? 0) >= MID_LIQUIDITY_SCORE) return 'mid';
  // Checked last so only a longtail leg can escalate.
  return ageTier(token) ?? 'longtail';
};

/** Pair tier = the riskier of the two legs. */
export const classifyPair = (
  sellToken: ClassifierToken | undefined | null,
  buyToken: ClassifierToken | undefined | null,
  chainId: number,
): SlippageTier => {
  const sellTier = tokenTier(sellToken, chainId);
  const buyTier = tokenTier(buyToken, chainId);
  return TIER_RANK[sellTier] >= TIER_RANK[buyTier] ? sellTier : buyTier;
};
